import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export default function ShareChildModal({ user, child, closeModal }) {
    const [code, setCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [timeLeft, setTimeLeft] = useState(900); // 15 minutes

    useEffect(() => {
        const generateCode = async () => {
            setLoading(true);
            setError('');
            try {
                if (!user?.uid) throw new Error('User not authenticated');
                if (!child?.id || !child?.name) throw new Error('Child data not available');

                const newCode = Math.floor(100000 + Math.random() * 900000).toString();
                const expiresAt = new Date(Date.now() + 15 * 60 * 1000); 

                await addDoc(collection(db, 'shareCodes'), {
                    code: newCode,
                    ownerUid: user.uid,
                    childId: child.id,
                    childName: child.name,
                    createdAt: Timestamp.now(),
                    expiresAt: Timestamp.fromDate(expiresAt),
                    claimedBy: null,
                    active: true
                });

                setCode(newCode);
            } catch (err) {
                console.error("Error generating share code:", err);
                setError(err.code === 'permission-denied' ? "Tidak memiliki izin." : "Gagal membuat kode berbagi.");
            } finally {
                setLoading(false);
            }
        };

        generateCode();
    }, [user, child]);

    useEffect(() => {
        if (code && timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [code, timeLeft]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercentage = (timeLeft / 900) * 100;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-5 font-sans">
            {/* Blurred Backdrop */}
            <div 
                className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
                onClick={closeModal}
            ></div>

            {/* Modal Content */}
            <div className="relative w-full max-w-sm bg-white/80 backdrop-blur-2xl rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white p-6 overflow-hidden transform transition-all">
                {/* Decorative blobs */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-sky-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60 pointer-events-none"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-indigo-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60 pointer-events-none"></div>

                <div className="relative z-10 text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm">
                        🤝
                    </div>
                    <h3 className="text-xl font-extrabold text-gray-800 tracking-tight mb-1">Bagikan Akses</h3>
                    <p className="text-xs text-gray-500 mb-6 font-medium px-4">
                        Berikan kode ini kepada tenaga kesehatan untuk memberikan akses sementara.
                    </p>
                    
                    <div className="mb-8">
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-6 space-y-4">
                                <div className="w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div>
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Membuat Kode Cerdas...</p>
                            </div>
                        )}
                        
                        {error && (
                            <div className="bg-rose-50/80 backdrop-blur-md border border-rose-200 rounded-[1.5rem] p-4 text-center">
                                <span className="text-2xl mb-2 block">⚠️</span>
                                <p className="font-bold text-rose-800 text-sm mb-1">Gagal Membuat Kode</p>
                                <p className="text-rose-600 text-xs">{error}</p>
                            </div>
                        )}
                        
                        {code && (
                            <div className="text-center">
                                <div className="bg-white/90 backdrop-blur-md rounded-[1.5rem] py-6 px-4 mb-5 border border-sky-100 shadow-[0_8px_30px_rgba(14,165,233,0.1)] relative overflow-hidden">
                                    <p className="text-[10px] font-bold text-sky-500 uppercase tracking-widest mb-2">Kode Akses Anda</p>
                                    <p className="text-5xl font-extrabold tracking-[0.2em] text-gray-800 drop-shadow-sm">{code}</p>
                                </div>

                                <div className="bg-gray-50/80 rounded-2xl p-4 border border-gray-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Waktu Tersisa</p>
                                        <p className="font-mono text-lg text-rose-500 font-extrabold">{formatTime(timeLeft)}</p>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                        <div 
                                            className={`h-full rounded-full transition-all duration-1000 ease-linear ${timeLeft < 300 ? 'bg-rose-500' : 'bg-sky-500'}`} 
                                            style={{ width: `${progressPercentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex space-x-3">
                        {error && (
                            <button 
                                onClick={() => window.location.reload()} 
                                className="flex-1 bg-gray-900 text-white font-bold py-3.5 rounded-xl hover:bg-gray-800 transition-colors shadow-lg"
                            >
                                Coba Lagi
                            </button>
                        )}
                        <button 
                            onClick={closeModal} 
                            className={`
                                ${error ? 'flex-1 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'w-full bg-gray-900 text-white hover:bg-gray-800 shadow-xl'} 
                                font-bold py-3.5 rounded-xl transition-all
                            `}
                        >
                            Tutup Jendela
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
